import * as nodePath from 'node:path';
import { generateId } from '@ai-sdk/provider-utils';
import { clientFetch } from '~/lib/.client/fetch';
import { BOLT_ATTACHMENTS_PREFIX } from '~/lib/constants';
import { workbenchStore } from '~/lib/stores/workbench';
import type { AttachmentsPostRequestBody, ClassifierResponse } from '~/routes/api.attachments';
import type { FileAttachment, Message } from '~/types/chat';
import { getFileTree } from '~/utils/files';
import { isInspectedImage } from '~/utils/inspect-element';
import { isBinaryFile } from '~/utils/is-binary-file';
import { logger } from '~/utils/logger';
import { plural } from '~/utils/plural';
import { removeWorkDir } from '~/utils/workDir';

const FILES_TO_INCLUDE = new RegExp(
  [
    // config files for various build tools
    /(vite|next|remix|svelte|astro|docusaurus|eleventy|app|metro|uno|rollup|tailwind|postcss|webpack)\.config\.(t|j|mj|cj)s$/,

    // eleventy config
    /\.eleventy\.js$/,

    // package files
    /(package|app)\.json$/,

    // typescript config
    /tsconfig.*\.json$/,

    // javascript config
    /jsconfig\.json$/,

    // netlify config
    /netlify\.toml$/,
  ]
    .map((pattern) => pattern.source)
    .join('|'),
);

export async function classifyAttachments(originalMessage: Message, fileAttachments: FileAttachment[]) {
  if (!originalMessage.experimental_attachments?.length) {
    return { proceed: true };
  }

  const message = {
    ...originalMessage,

    // we don't need to send the actual attachments because we only classify based on the user prompt
    experimental_attachments: [],
  };

  const fileMap = workbenchStore.files.get();
  const fileTree = getFileTree(fileMap);

  const attachments = new Map<string, FileAttachment>();

  const inspectedElements = [];

  for (const attachment of fileAttachments) {
    const { name } = attachment.file;

    if (isInspectedImage(name)) {
      inspectedElements.push(name);
    } else {
      /**
       * We generate a unique id so we can always reliably match the attachments
       * even if multiple files have the same name.
       */
      attachments.set(`${generateId()}.${name}`, attachment);
    }
  }

  const files = Object.entries(fileMap).reduce(
    (acc, [filePath, entry]) => {
      if (entry?.type === 'file' && !entry.isBinary && FILES_TO_INCLUDE.test(filePath)) {
        acc.push({ filePath: removeWorkDir(filePath), content: entry.content });
        return acc;
      }

      return acc;
    },
    [] as NonNullable<AttachmentsPostRequestBody['files']>,
  );

  const body: AttachmentsPostRequestBody = {
    message,
    fileTree,
    files,
    inspectedElements,
    attachments: [...attachments.keys()],
  };

  let result: ClassifierResponse | null;

  try {
    const response = await clientFetch('/api/attachments', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    result = await response.json<ClassifierResponse | null>();

    if (!response.ok || !result) {
      return { proceed: true };
    }
  } catch (error) {
    logger.error(error);

    return { proceed: true };
  }

  const filesToAdd = result.attachments.flatMap((classifiedAttachment) => {
    if (classifiedAttachment.add && classifiedAttachment.targetPath) {
      const attachmentToAdd = attachments.get(classifiedAttachment.fileName);

      if (attachmentToAdd) {
        return { dest: classifiedAttachment.targetPath, attachment: attachmentToAdd };
      }
    }

    return [];
  });

  return { proceed: result.continue, files: filesToAdd };
}

export async function saveAttachments(attachments?: Array<{ dest: string; attachment: FileAttachment }>) {
  if (!attachments || attachments.length === 0) {
    return undefined;
  }

  const renamedFiles = new Map<string, string>();

  const settledWrites = await Promise.allSettled(
    attachments.map(async ({ dest, attachment }) => {
      const arrayBuffer = await attachment.file.arrayBuffer();
      const content = new Uint8Array(arrayBuffer);

      const originalPath = nodePath.join(dest, attachment.file.name);

      const targetPath = workbenchStore.generateUniqueName(originalPath, 'file');

      if (targetPath !== originalPath) {
        renamedFiles.set(attachment.file.name, nodePath.basename(targetPath));
      }

      await workbenchStore.writeFile(targetPath, {
        type: 'file',
        isBinary: isBinaryFile(content),
        content,
      });

      return targetPath;
    }),
  );

  const successfulWrites = settledWrites.filter(({ status }) => status === 'fulfilled');

  if (successfulWrites.length === 0) {
    return undefined;
  }

  const assistantContent = [
    `I have added the following ${plural(successfulWrites.length, 'file')} to the project:`,
    '',
    settledWrites
      .flatMap((result) => {
        if (result.status === 'fulfilled') {
          return `  - ${result.value}`;
        }

        return [];
      })
      .join('\n'),
  ].join('\n');

  const newMessages: Message[] = [
    {
      id: `${BOLT_ATTACHMENTS_PREFIX}${generateId()}`,
      role: 'assistant',
      content: assistantContent,
      type: 'chat',
      ignore: true,
      status: 'complete',
      parts: [
        {
          type: 'text',
          text: assistantContent,
        },
      ],
    },

    /**
     * We need to add a dummy user message that we will ignore and hide. This is necessary
     * because, upon reloading, the AI SDK removes the assistant message, as it requires
     * a user message to be the last one.
     *
     * @see https://github.com/vercel/ai/blob/9b09f85143986636570e597c31903daf160608cd/packages/react/src/use-chat.ts#L452-L453
     */
    {
      id: generateId(),
      role: 'user',
      content: '',
      type: 'chat',
      ignore: true,
      hidden: true,
      status: 'complete',
      parts: [],
    },
  ];

  return {
    newMessages,
    renamedFiles,
  };
}
